/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {Route, Switch, useRouteMatch} from 'react-router'
import OrderHistory from './order-history'
import OrderDetail from './order-detail'

/**
 * AccountOrders component is responsible for rendering the user's order history and order details
 * within the account section of the application. It utilizes React Router to switch between
 * displaying the order history and the details of a specific order based on the URL path.
 *
 * @component
 * @returns {JSX.Element} A JSX element containing the routing logic for order history and order details.
 *
 * @example
 * // Usage in a parent component
 * <AccountOrders />
 *
 * The component uses the `useRouteMatch` hook to access the current path and sets up two routes:
 * - The default route (`path`) renders the `OrderHistory` component, displaying a list of all orders.
 * - The route with a dynamic segment (`${path}/:orderNo`) renders the `OrderDetail` component,
 *   showing details for a specific order identified by `orderNo`.
 */
const AccountOrders = () => {
    const {path} = useRouteMatch()

    return (
        <Switch>
            <Route exact path={path}>
                <OrderHistory />
            </Route>
            <Route exact path={`${path}/:orderNo`}>
                <OrderDetail />
            </Route>
        </Switch>
    )
}

AccountOrders.getTemplateName = () => 'account-order-history'

export default AccountOrders
